import React, { useRef, useEffect, useState, useCallback } from "react";
import { useCompanyDistributorData } from "../../CompanyData";
import "../../index.scss";

const RelationshipView = () => {
  const {
    parents,
    distributors,
    totalParents,
    totalDistributors,
    noParentCount,
    search,
    setSearch,
    selectedParentId,
    setSelectedParentId,
    sortParents,
    expandedGroups,
    toggleExpand,
    toggleSortParents,
    isLoading,
  } = useCompanyDistributorData();

  const svgContainerRef = useRef(null);
  const leftListRef = useRef(null);
  const rightListRef = useRef(null);
  const [connections, setConnections] = useState([]);

  const calculateConnections = useCallback(() => {
    if (
      !svgContainerRef.current ||
      !leftListRef.current ||
      !rightListRef.current
    )
      return;

    const svgRect = svgContainerRef.current.getBoundingClientRect();
    const leftListRect = leftListRef.current.getBoundingClientRect();
    const rightListRect = rightListRef.current.getBoundingClientRect();

    const leftTop = leftListRect.top;
    const leftBottom = leftListRect.bottom;
    const rightTop = rightListRect.top;
    const rightBottom = rightListRect.bottom;

    const leftItems = leftListRef.current.querySelectorAll("[data-parent-id]");
    const rightItems =
      rightListRef.current.querySelectorAll("[data-dist-parent]");
    const newConnections = [];

    const toSvgCoords = (el, side = "right") => {
      const rect = el.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      return {
        x:
          side === "right"
            ? rect.right - svgRect.left
            : rect.left - svgRect.left,
        y: midY - svgRect.top,
        absY: midY,
      };
    };

    leftItems.forEach((leftEl) => {
      const parentId = leftEl.getAttribute("data-parent-id");
      const parent = parents.find((p) => p.id === parentId);

      if (
        parentId !== "no-parent" &&
        (!parent || parent.distributorsCount === 0)
      )
        return;

      const start = toSvgCoords(leftEl, "right");
      if (start.absY < leftTop || start.absY > leftBottom) return;

      const matchingRights = Array.from(rightItems).filter(
        (el) => el.getAttribute("data-dist-parent") === parentId,
      );
      if (matchingRights.length === 0) return;

      const visibleRights = matchingRights.filter((el) => {
        const mid =
          el.getBoundingClientRect().top +
          el.getBoundingClientRect().height / 2;
        return mid >= rightTop && mid <= rightBottom;
      });
      if (visibleRights.length === 0) return;

      visibleRights.sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
      );

      const color = parentId === "no-parent" ? "#94A3B8" : parent.color;
      const isSelected = selectedParentId === parentId;
      const isDashed = parentId === "no-parent";

      visibleRights.forEach((rightEl, idx) => {
        const end = toSvgCoords(rightEl, "left");
        const total = visibleRights.length;
        const midIndex = (total - 1) / 2;
        const offsetFactor = (idx - midIndex) * 10;
        const dx = end.x - start.x;
        const controlX1 = start.x + Math.min(Math.max(dx * 0.4, 40), 120);
        const controlX2 = end.x - Math.min(Math.max(dx * 0.4, 40), 120);
        const controlY1 = start.y + offsetFactor * 0.25;
        const controlY2 = end.y + offsetFactor * 0.12;
        const path = `M ${start.x} ${start.y} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${end.x} ${end.y}`;

        newConnections.push({
          id: `${parentId}-${rightEl.getAttribute("data-dist-id") || idx}`,
          path,
          startX: start.x,
          startY: start.y,
          endX: end.x,
          endY: end.y,
          color,
          dashed: isDashed,
          isSelected,
          opacity: isSelected ? 0.9 : 0.55,
          strokeWidth: isSelected ? 2.5 : 1.8,
        });
      });
    });

    setConnections(newConnections);
  }, [selectedParentId, parents]);

  useEffect(() => {
    const run = () => requestAnimationFrame(calculateConnections);
    run();

    const resizeObserver = new ResizeObserver(run);
    if (svgContainerRef.current)
      resizeObserver.observe(svgContainerRef.current);
    if (leftListRef.current) resizeObserver.observe(leftListRef.current);
    if (rightListRef.current) resizeObserver.observe(rightListRef.current);

    window.addEventListener("resize", run);
    const leftEl = leftListRef.current;
    const rightEl = rightListRef.current;
    leftEl?.addEventListener("scroll", run);
    rightEl?.addEventListener("scroll", run);
    const timer = setTimeout(run, 120);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", run);
      leftEl?.removeEventListener("scroll", run);
      rightEl?.removeEventListener("scroll", run);
      clearTimeout(timer);
    };
  }, [
    calculateConnections,
    distributors,
    parents,
    expandedGroups,
    selectedParentId,
  ]);

  const getParentColor = (parentId) => {
    const p = parents.find((x) => x.id === parentId);
    return p ? p.color : "#94A3B8";
  };

  const getParentBg = (parentId) => {
    const p = parents.find((x) => x.id === parentId);
    return p ? p.bg : "#F8FAFC";
  };

  if (isLoading) {
    return (
      <div className="relationship-view">
        <div className="pc-loading">Loading relationships…</div>
      </div>
    );
  }

  return (
    <div className="relationship-view">
      {/* Header */}
      <header className="rv-header">
        {/* <div className="rv-header-left">
          <div className="rv-logo">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="3" stroke="#3B82F6" strokeWidth="2" />
              <path
                d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6"
                stroke="#3B82F6"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle
                cx="18"
                cy="10"
                r="2.5"
                stroke="#3B82F6"
                strokeWidth="1.5"
              />
              <path
                d="M16 18c1.5-1.5 3-2 5-2"
                stroke="#3B82F6"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <h1>Company - Distributor Relationship View</h1>
            <p>
              Visualize and manage the relationship between companies (parent)
              and their distributors (child)
            </p>
          </div>
        </div> */}

        <div className="rv-header-right">
          <div className="rv-search">
            <input
              type="text"
              placeholder="Search company or distributor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <svg
              className="search-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle cx="11" cy="11" r="7" stroke="#94A3B8" strokeWidth="2" />
              <path
                d="M20 20l-3-3"
                stroke="#94A3B8"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          {/* <button className="rv-btn active">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 6h16M4 12h10M4 18h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Relationship View
          </button>
          <button className="rv-btn icon-only">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button> */}
        </div>
      </header>

      {/* Main Content */}
      <div className="rv-main">
        {/* Left Panel - Parent Companies */}
        <div className="rv-panel rv-panel-left">
          <div className="rv-panel-header">
            <div className="rv-panel-title">
              <span className="icon blue">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="3"
                    width="7"
                    height="7"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <rect
                    x="14"
                    y="3"
                    width="7"
                    height="7"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <rect
                    x="3"
                    y="14"
                    width="7"
                    height="7"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <rect
                    x="14"
                    y="14"
                    width="7"
                    height="7"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </span>
              Parent Companies
              <span className="count blue">{totalParents}</span>
            </div>
            <div className="rv-panel-actions">
              <button className="sort-btn" onClick={toggleSortParents}>
                A-Z {sortParents === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>

          <div className="rv-list" ref={leftListRef}>
            {parents.map((parent) => (
              <div
                key={parent.id}
                className={`rv-card parent-card ${selectedParentId === parent.id ? "selected" : ""}`}
                data-parent-id={parent.id}
                onClick={() => setSelectedParentId(parent.id)}
                style={{
                  borderColor:
                    selectedParentId === parent.id ? parent.color : undefined,
                  background:
                    selectedParentId === parent.id ? parent.bg : undefined,
                }}
              >
                <div className="card-left">
                  <div
                    className="avatar"
                    style={{ background: parent.bg, color: parent.color }}
                  >
                    {parent.code}
                  </div>
                  <div className="info">
                    <div className="name">{parent.name}</div>
                    <div className="sub">No Parents</div>
                  </div>
                </div>
                <div className="card-right">
                  <span
                    className="count-pill"
                    style={{ background: parent.bg, color: parent.color }}
                  >
                    {parent.distributorsCount > 9
                      ? "8+"
                      : parent.distributorsCount}
                  </span>
                  <span className="arrow">›</span>
                </div>
              </div>
            ))}

            {/* No Parent */}
            <div
              className="rv-card parent-card no-parent-card"
              data-parent-id="no-parent"
            >
              <div className="card-left">
                <div className="avatar gray">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="9"
                      cy="8"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M3 19c0-3 2.5-5 6-5s6 2 6 5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div className="info">
                  <div className="name">No Parent</div>
                  <div className="sub">
                    Distributors not linked to any parent
                  </div>
                </div>
              </div>
              <div className="card-right">
                <span className="count-pill gray">{noParentCount}</span>
                <span className="arrow">›</span>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Lines */}
        <div className="rv-connections" ref={svgContainerRef}>
          <div className="connection-label">
            <span>← Shows relationship connection →</span>
          </div>
          <svg
            className="connection-svg"
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              inset: 0,
            }}
          >
            {connections.map((conn) => (
              <g key={conn.id}>
                <path
                  d={conn.path}
                  fill="none"
                  stroke={conn.color}
                  strokeWidth={conn.strokeWidth}
                  strokeOpacity={conn.opacity}
                  strokeDasharray={conn.dashed ? "6 4" : "0"}
                  strokeLinecap="round"
                />
                <circle
                  cx={conn.startX}
                  cy={conn.startY}
                  r={conn.isSelected ? 4.5 : 3.5}
                  fill={conn.color}
                  fillOpacity={conn.isSelected ? 0.95 : 0.7}
                />
                <circle
                  cx={conn.endX}
                  cy={conn.endY}
                  r={conn.isSelected ? 4 : 3}
                  fill={conn.color}
                  fillOpacity={conn.isSelected ? 0.9 : 0.6}
                />
              </g>
            ))}
          </svg>
        </div>

        {/* Right Panel - Distributors */}
        <div className="rv-panel rv-panel-right">
          <div className="rv-panel-header">
            <div className="rv-panel-title">
              <span className="icon green">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="8"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              Distributors
              <span className="count green">{totalDistributors}</span>
            </div>
            <div className="rv-panel-actions">
              <button className="sort-btn">A-Z ↕</button>
            </div>
          </div>

          <div className="rv-list" ref={rightListRef}>
            {distributors.map((item) => {
              if (item.type === "more") {
                return (
                  <div
                    key={item.id}
                    className="rv-card more-card"
                    onClick={() => toggleExpand(item.parentId)}
                  >
                    <div className="more-content">
                      <span className="more-title">
                        +{item.count} More Distributors
                      </span>
                      <span className="more-sub">
                        Total {item.total} Distributors
                      </span>
                    </div>
                    <span className="arrow">›</span>
                  </div>
                );
              }

              if (item.type === "no-parent-header") {
                return (
                  <div key={item.id} className="rv-section-header">
                    <div className="section-left">
                      <span className="section-icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            cx="9"
                            cy="8"
                            r="3"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M3 19c0-3 2.5-5 6-5s6 2 6 5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                      Distributors with No Parent ({item.count})
                    </div>
                    <span className="section-count">{item.count}</span>
                  </div>
                );
              }

              if (item.type === "more-no-parent") {
                return (
                  <div
                    key={item.id}
                    className="rv-more-link"
                    onClick={() => toggleExpand("no-parent")}
                  >
                    +{item.count} More Distributors
                  </div>
                );
              }

              const parentColor = getParentColor(item.parentId);
              const parentBg = getParentBg(item.parentId);
              const isSelectedGroup = selectedParentId === item.parentId;

              return (
                <div
                  key={item.id}
                  className={`rv-card dist-card ${isSelectedGroup ? "selected" : ""}`}
                  data-dist-parent={item.parentId || "no-parent"}
                  data-dist-id={item.id}
                  style={{
                    borderColor: isSelectedGroup ? parentColor : undefined,
                    background: isSelectedGroup ? parentBg : undefined,
                  }}
                >
                  <div className="card-left">
                    <div
                      className="avatar"
                      style={{ background: parentBg, color: parentColor }}
                    />
                    <div className="info">
                      <div className="name">{item.name}</div>
                    </div>
                  </div>
                  {/* Status from API */}
                  <div className="card-right">
                    <span
                      className={`status${item.isActive !== false ? " active" : " inactive"}`}
                    >
                      <span
                        className="dot"
                        style={
                          item.isActive === false
                            ? { background: "#94a3b8" }
                            : undefined
                        }
                      />
                      {item.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="rv-legend">
        {parents.map((parent) => (
          <div
            key={parent.id}
            className="legend-item"
            style={{ cursor: "pointer" }}
            onClick={() => setSelectedParentId(parent.id)}
          >
            <span className="line" style={{ background: parent.color }} />
            {parent.name} (
            {parent.distributorsCount > 9 ? "12+" : parent.distributorsCount})
          </div>
        ))}
        {noParentCount > 0 && (
          <div className="legend-item">
            <span className="line dashed" />
            No Parent ({noParentCount})
          </div>
        )}
      </div>

      <div className="rv-footer-note">
        <span className="info-icon">ℹ</span>
        Each line represents the relationship between a parent company and its
        distributor(s). Distributors in "No Parent" are not linked to any
        company.
      </div>
    </div>
  );
};

export default RelationshipView;
